import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth, gradeAtLeast } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { AlertTriangle, Upload, ArrowLeft, Save, X } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 2 * 1024 * 1024;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PantheonEditPage({ mode = "create" }) {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [imageData, setImageData] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [permissionOk, setPermissionOk] = useState(true);

  useEffect(() => {
    if (mode === "create") {
      if (!gradeAtLeast(user, "Neophyte")) {
        setPermissionOk(false);
      }
      return;
    }
    // edit mode — fetch and verify worshipper
    (async () => {
      try {
        const { data } = await api.get(`/gods/${id}`);
        if (!data.user_is_worshipper && user?.role !== "admin") {
          setPermissionOk(false);
        } else {
          setName(data.name);
          setText(data.text);
          setImageData(data.image_data);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        navigate("/pantheon");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, mode, user, navigate]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("pantheon.file_not_image"));
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("pantheon.file_too_large"));
      e.target.value = "";
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(file);
      setImageData(dataUrl);
    } catch (err) {
      toast.error("Could not read file");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim() || !imageData) {
      toast.error(t("pantheon.no_image_yet"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "create") {
        const { data } = await api.post("/gods", { name: name.trim(), text: text.trim(), image_data: imageData });
        toast.success(t("pantheon.created_toast"));
        navigate(`/pantheon/${data.id}`);
      } else {
        const { data } = await api.put(`/gods/${id}`, { name: name.trim(), text: text.trim(), image_data: imageData });
        toast.success(t("pantheon.saved_toast"));
        navigate(`/pantheon/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <main className="max-w-3xl mx-auto px-6 md:px-10 py-16"><p className="text-[#7A7061] italic">{t("common.loading")}</p></main>;
  }
  if (!permissionOk) {
    return (
      <main className="max-w-2xl mx-auto px-6 md:px-10 py-16" data-testid="pantheon-no-permission">
        <Link to="/pantheon" className="btn-ghost inline-flex items-center gap-2 mb-8"><ArrowLeft size={14} /> {t("pantheon.back_link")}</Link>
        <div className="border border-[#C1B49A] p-12 text-center">
          <h2 className="font-display text-3xl italic text-[#8B3A3A] mb-3">{t("lectures.forbidden_title")}</h2>
          <p className="text-[#4A4238]">{mode === "create" ? t("pantheon.cant_enshrine") : "Only worshippers may edit this god."}</p>
        </div>
      </main>
    );
  }

  return (
    <main data-testid={`pantheon-${mode}-page`} className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <Link to={mode === "edit" ? `/pantheon/${id}` : "/pantheon"} className="btn-ghost inline-flex items-center gap-2 mb-8" data-testid="pantheon-back-link">
        <ArrowLeft size={14} /> {t("pantheon.back_link")}
      </Link>

      <div
        className="border-2 border-[#8B3A3A] bg-[#F4EEDF] p-6 mb-10 flex items-start gap-4 anim-fade-up"
        data-testid="pantheon-anti-rapport-warning"
      >
        <AlertTriangle size={28} className="text-[#8B3A3A] flex-shrink-0 mt-1" />
        <div>
          <p className="label-small text-[#8B3A3A] mb-2">{t("pantheon.anti_rapport_title")}</p>
          <p className="text-[15px] text-[#1A1918] leading-relaxed">{t("pantheon.anti_rapport_body")}</p>
        </div>
      </div>

      <form onSubmit={submit} className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10 space-y-8" data-testid="pantheon-form">
        <div>
          <label className="label-small block mb-2">{t("pantheon.name_label")}</label>
          <input data-testid="pantheon-name-input" className="input-etched font-display text-2xl" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>

        <div>
          <label className="label-small block mb-2">{t("pantheon.image_label")}</label>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div
              className="w-40 h-40 border border-[#C1B49A] bg-[#F4EEDF] flex items-center justify-center flex-shrink-0"
              style={{
                backgroundImage: imageData ? `url(${imageData})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="pantheon-image-preview"
            >
              {!imageData && <span className="text-xs text-[#7A7061] italic px-2 text-center">{t("pantheon.no_image_yet")}</span>}
            </div>
            <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
              <Upload size={14} />
              <span>{imageData ? t("pantheon.change_image") : t("pantheon.choose_image")}</span>
              <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="pantheon-image-input" />
            </label>
          </div>
        </div>

        <div>
          <label className="label-small block mb-2">{t("pantheon.text_label")}</label>
          <textarea
            data-testid="pantheon-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={14}
            className="textarea-parchment"
            required
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Link to={mode === "edit" ? `/pantheon/${id}` : "/pantheon"} className="btn-secondary inline-flex items-center gap-2">
            <X size={14} /> {t("pantheon.cancel")}
          </Link>
          <button data-testid="pantheon-submit-btn" type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2">
            <Save size={14} />
            {busy
              ? (mode === "create" ? t("pantheon.busy_creating") : t("pantheon.saving"))
              : (mode === "create" ? t("pantheon.submit_create") : t("pantheon.submit_save"))}
          </button>
        </div>
      </form>
    </main>
  );
}
